import "@liveblocks/react-ui/styles.css"

import { LiveObject, type LsonObject, type Room } from "@liveblocks/client"
import {
  type JsonObject,
  LiveblocksProvider,
  RoomProvider,
  useMutation,
  useStorage,
} from "@liveblocks/react"
import type { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import { createContext, type PropsWithChildren, useContext } from "react"

import { useCollaborationRoomFlag } from "../../app/integrations/AmplitudeExperiments"
import { getRepositoryName } from "../../app/routing"
import {
  authenticateLiveblocks,
  resolveMentionSuggestions,
  resolveUsers,
} from "../../app/services/liveblocks"
import { useRepository } from "../../app/services/repository"
import { type ExistingDocumentData } from "../documentData"
import { useExistingDocument } from "../DocumentProvider"

export function CollaborationRoom(props: PropsWithChildren) {
  const { children } = props

  const { authors } = useRepository()

  if (useCollaborationRoomFlag() === "off") {
    return <>{children}</>
  }

  return (
    <LiveblocksProvider
      authEndpoint={authenticateLiveblocks}
      resolveUsers={({ userIds }) => resolveUsers({ userIds, authors })}
      resolveMentionSuggestions={({ text }) =>
        resolveMentionSuggestions({ text, authors })
      }
    >
      <CollaborationRoomInner>{children}</CollaborationRoomInner>
    </LiveblocksProvider>
  )
}

// We split CollaborationRoom and CollaborationRoomInner so the latter can call useClient with a matching LiveblocksProvider parent.
function CollaborationRoomInner(props: PropsWithChildren) {
  const { children } = props
  const { documentId, version } = useExistingDocument()

  const versionId =
    version.status === "release"
      ? `${version.version_id}/${version.release_id}`
      : version.version_id

  return (
    <RoomProvider
      id={`${getRepositoryName()}/${documentId}/${versionId}`}
      initialStorage={{ document: new LiveObject({} as JsonObject) }} // TODO: This temporary data should be replaced once we introduce full RTC support through Liveblocks.
    >
      {children}
    </RoomProvider>
  )
}

/**
 * Similar to Liveblock's useStorage but optional and not throwing.
 * Indeed, the presence of a room above the component hierarchy is optional for Page Builder.
 */
export function useCollaborationStorage() {
  // This is a workaround to avoid using the liveblocks storage.
  // TODO: This temporary fix should be replaced once we introduce full RTC support through Liveblocks.
  return undefined

  try {
    const value = (useStorage((_) => _) as { document: DocumentT }) ?? undefined

    // This is actually safe. The try catch is here in case a collaboration room is not applicable in the current feature flag context.
    // If we change version and a room becomes available, this hook will be a brand new one nested inside a newly mounted CollaborationRoomInner parent.
    // If the room is not available, the first hook in the try catch will always throw. If the room is available, neither hook will ever throw.
    // Thus, that hook is not conditional.

    const update = useMutation(({ storage }, localData: DocumentT) => {
      storage.set("document", new LiveObject(localData as LsonObject))
    }, [])

    return { value, update }
  } catch {
    return undefined
  }
}

/** Internal, don't use outside ExistingDocument. */
export function useInitCollaborationRoom() {
  return useContext(initRoomContext)
}

type InitRoomContextValue = (initData: ExistingDocumentData) => Room

const initRoomContext = createContext<InitRoomContextValue | undefined>(
  undefined,
)

declare global {
  interface Liveblocks {
    Storage: {
      // Note: We keep casting LsonObject from/to DocumentT.
      // DocumentT looks like a very complicated type, generated by io-ts but it IS compatible with a plain/serializable JSON/LSON type, in reality.
      document: LiveObject<LsonObject>
    }
  }
}
